"use client";

import { Button } from "@dionatanbig2be/big2be-ui";
import { FiArrowLeft } from "react-icons/fi";
import { useAuthFormFields } from "@/shell/hooks/useBootstrap";
import { ForgotPasswordForm } from "./ForgotPasswordForm";
import { LoginForm } from "./LoginForm";
import { RegisterForm } from "./RegisterForm";
import { useAuthModal } from "../stores/useAuthModal";

/**
 * Miolo do fluxo de auth (login → cadastro / recuperação), sem moldura.
 * Compartilhado pelo AuthModal e pelo LoginGate (loja que exige login): a
 * view e o step vivem na store do modal nos dois casos — o gate só ignora o
 * `open`.
 */
export const AUTH_VIEWS = {
    login: {
        title: "Acessar sua conta",
        box: "w-full max-w-none rounded-t-2xl md:max-w-md md:rounded-box",
        footer: { text: "Não tem uma conta?", action: "Criar nova conta", goTo: "register" },
    },
    register: {
        title: "Criar nova conta",
        box: "w-full max-w-none rounded-t-2xl md:max-w-2xl md:rounded-box",
        footer: { text: "Já tem uma conta?", action: "Fazer login", goTo: "login" },
    },
    recovery: {
        title: "Recuperar senha",
        box: "w-full max-w-none rounded-t-2xl md:max-w-md md:rounded-box",
        footer: { text: "Lembrou sua senha?", action: "Voltar ao login", goTo: "login" },
    },
} as const;

/**
 * Destino pedido em `?redirect=<rota>` (o proxy anexa ao barrar rota privada).
 * Só rotas internas — evita open redirect.
 */
export function readSafeRedirect(): string | null {
    if (typeof window === "undefined") return null;
    const redirect = new URLSearchParams(window.location.search).get("redirect");
    return redirect && redirect.startsWith("/") && !redirect.startsWith("//") ? redirect : null;
}

/**
 * No step da senha, o título ganha uma seta de voltar que retorna ao step do
 * identificador. Na recuperação de senha a mesma seta volta pro login — é de
 * lá que se entra no fluxo.
 */
export function AuthFlowTitle() {
    const { view, loginStep, setView, setLoginStep } = useAuthModal();
    const showBack = (view === "login" && loginStep === "password") || view === "recovery";

    return (
        <span className="flex items-center gap-2">
            {showBack && (
                <button
                    type="button"
                    onClick={() =>
                        view === "recovery" ? setView("login") : setLoginStep("identifier")
                    }
                    aria-label="Voltar"
                    className="-ml-1 flex cursor-pointer items-center justify-center rounded-full p-1.5 text-base-content/70 transition-colors hover:bg-base-200 hover:text-base-content"
                >
                    <FiArrowLeft size={18} />
                </button>
            )}
            <span className="text-secondary">{AUTH_VIEWS[view].title}</span>
        </span>
    );
}

export function AuthFlowBody({ onSuccess }: { onSuccess: () => void }) {
    const { view, setView } = useAuthModal();
    const current = AUTH_VIEWS[view];
    // Loja cadastra o cliente por conta própria — sem caminho pra criar conta novo pelo site.
    const registrationBlocked = useAuthFormFields().register.blocked;
    const hideFooter = view === "login" && registrationBlocked;

    return (
        <>
            {view === "login" ? (
                <LoginForm onSuccess={onSuccess} />
            ) : view === "recovery" ? (
                <ForgotPasswordForm onAuthenticated={onSuccess} onDone={() => setView("login")} />
            ) : (
                <RegisterForm onSuccess={onSuccess} />
            )}

            {!hideFooter && (
                <p className="mt-4 text-center text-sm opacity-70">
                    {current.footer.text}{" "}
                    <Button
                        tone="secondary"
                        variant="link"
                        size="sm"
                        onClick={() => setView(current.footer.goTo)}
                    >
                        {current.footer.action}
                    </Button>
                </p>
            )}
        </>
    );
}
